// Batch B1 — inline fact-checker confidence badge for chat message bubbles.
//
// The chat API attaches a fact-checker `verification` block to every
// assistant reply (see `app/routers/chat.py::VerificationBlock` and the
// WS `reply` frame). Shape:
//   { verdict, confidence, message_id }
//   verdict     — categorical, e.g. ACCURATE / PARTIALLY_ACCURATE /
//                 INACCURATE / NOT_VERIFIABLE (null when fact-checking off /
//                 on the fast rule-based path).
//   confidence  — 0.0-1.0 float; the best retrieved-chunk grounding score.
//
// Until now the chat UI rendered only the reply text, so the user never
// saw how well-grounded the answer was. This component surfaces it as a
// compact colour-coded pill ("Verified · 94%") beneath the assistant
// bubble — green for a confident ACCURATE verdict, amber for partial /
// low-confidence, red for INACCURATE, grey for not-verifiable / unknown.
//
// Pure presentation. It renders NOTHING unless `verification` is present
// AND `verification.confidence` is a real number — so fast-path replies
// (verdict but no numeric grounding score) and fact-checking-disabled
// agents show no pill.

import React from "react";
import { COLORS } from "../theme/colors";

// No `COLORS.red` token exists in the palette (see CLAUDE.md) — use the
// repo's `COLORS.red || "#ef4444"` fallback idiom.
const RED = COLORS.red || "#ef4444";

/**
 * Derive the pill's label + colour from the verification block, or return
 * null when no badge should render.
 *
 * @param {{verdict?: string, confidence?: number}|null|undefined} verification
 * @returns {{label: string, color: string, pct: number, verdict: string}|null}
 */
export function computeVerificationBadge(verification) {
  if (!verification) return null;
  const { verdict, confidence } = verification;
  // Only render when confidence is a real number (0.0-1.0 grounding score).
  if (typeof confidence !== "number" || Number.isNaN(confidence)) return null;

  const pct = Math.round(confidence * 100);
  const norm = String(verdict || "").toUpperCase();

  let label;
  let color;
  if (norm === "INACCURATE") {
    label = "Inaccurate";
    color = RED;
  } else if (norm === "PARTIALLY_ACCURATE" || norm === "PARTIAL") {
    label = "Partial";
    color = COLORS.orange;
  } else if (norm === "ACCURATE") {
    // A confident ACCURATE verdict is green; a low-confidence one is amber.
    label = "Verified";
    color = confidence >= 0.5 ? COLORS.green : COLORS.orange;
  } else if (norm === "NOT_VERIFIABLE") {
    label = "Unverified";
    color = COLORS.textDim;
  } else {
    // Unknown / missing verdict but we still have a grounding score.
    label = "Checked";
    color = COLORS.textDim;
  }

  return { label, color, pct, verdict: norm };
}

/**
 * Compact fact-checker confidence pill rendered inside assistant bubbles.
 */
export function VerificationBadge({ verification }) {
  const badge = computeVerificationBadge(verification);
  if (!badge) return null;

  const { label, color, pct, verdict } = badge;
  const title = `Fact-checker verdict: ${verdict || "unknown"} · grounding confidence ${pct}%`;

  return (
    <div style={{ marginTop: 6, display: "flex" }}>
      <span
        title={title}
        aria-label={title}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          padding: "2px 8px",
          fontSize: 11,
          fontWeight: 600,
          lineHeight: 1.4,
          borderRadius: 10,
          border: `1px solid ${color}40`,
          background: `${color}15`,
          color,
          fontFamily: "inherit",
        }}
      >
        <span
          aria-hidden="true"
          style={{ width: 6, height: 6, borderRadius: "50%", background: color, flexShrink: 0 }}
        />
        {label} · {pct}%
      </span>
    </div>
  );
}

export default VerificationBadge;
